import { useRef, useState, useCallback, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { productCategories } from '@/mocks/products';

interface ProductCarouselProps {
  categoryIndex: number;
}

export default function ProductCarousel({ categoryIndex }: ProductCarouselProps) {
  const { t } = useTranslation();
  const category = productCategories[categoryIndex];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [scrollStart, setScrollStart] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  const scrollBy = (dir: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 320, behavior: 'smooth' });
    setTimeout(checkScroll, 350);
  };

  const handleMouseDown = (e: MouseEvent) => {
    setDragStart(e.clientX);
    setScrollStart(scrollRef.current?.scrollLeft || 0);
    setIsDragging(false);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (dragStart === null) return;
    const diff = dragStart - e.clientX;
    if (Math.abs(diff) > 5) setIsDragging(true);
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollStart + diff;
    }
  };

  const handleMouseUp = () => {
    setDragStart(null);
    checkScroll();
  };

  const handleMouseLeave = () => {
    if (dragStart !== null) {
      setDragStart(null);
      checkScroll();
    }
  };

  if (category.products.length === 0) return null;

  return (
    <section className="py-8 md:py-10">
      <div className="px-4 md:px-6 lg:px-8 xl:px-12">
        <div className="flex items-center justify-between mb-4 md:mb-5">
          <h2 className="text-lg md:text-xl font-semibold text-foreground-900 tracking-tight">
            {category.name}
          </h2>
          <a
            href={category.link}
            target="_blank"
            rel="noopener nofollow"
            className="text-xs md:text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
          >
            {t('products.viewAll')}
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          </a>
        </div>

        <div className="relative group">
          {canScrollLeft && (
            <button
              onClick={() => scrollBy(-1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/90 shadow-sm border border-background-200/70 flex items-center justify-center text-foreground-600 hover:text-primary-600 transition-colors opacity-0 group-hover:opacity-100"
              aria-label="Scroll left"
            >
              <i className="ri-arrow-left-s-line w-5 h-5 flex items-center justify-center" />
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scrollBy(1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-background-50/90 shadow-sm border border-background-200/70 flex items-center justify-center text-foreground-600 hover:text-primary-600 transition-colors opacity-0 group-hover:opacity-100"
              aria-label="Scroll right"
            >
              <i className="ri-arrow-right-s-line w-5 h-5 flex items-center justify-center" />
            </button>
          )}

          <div
            ref={scrollRef}
            onScroll={checkScroll}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            className="flex gap-3 md:gap-4 overflow-x-auto pb-2 scrollbar-hide cursor-grab active:cursor-grabbing"
          >
            {category.products.map((product) => (
              <a
                key={product.id}
                href={product.link}
                target="_blank"
                rel="noopener nofollow"
                onClick={(e) => {
                  if (isDragging) e.preventDefault();
                }}
                className="flex-shrink-0 w-[160px] md:w-[220px] group/card"
              >
                <div className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden transition-all duration-300 hover:shadow-md hover:border-background-300/80">
                  <div className="aspect-square bg-background-100 overflow-hidden relative">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-105"
                      draggable={false}
                    />
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-xs md:text-sm font-medium text-foreground-900 leading-snug line-clamp-2 mb-2 h-9 md:h-10">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-sm md:text-base font-bold text-primary-600">
                        {product.price}
                      </span>
                      <span className="text-[10px] md:text-xs font-medium text-background-50 bg-primary-500 px-2 py-1 rounded-full whitespace-nowrap">
                        {t('products.buy')}
                      </span>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}